import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PriceId } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';
import {
  useLocation,
  useMatches,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import type { Cart, EmptyCart, Store, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscountPercentage,
  getMaxCartQuantity,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

import {
  useAddProductOnSuccess,
  useAddProductToBag,
  useGetProductPrices,
} from '../../../hooks';
import { useProductTracking, useSearchContext } from '../../../providers';
import type { OptionalMenuRouteParams } from '../../../types';
import { getSource, isSupportedStoreSpecial } from '../../../util';
import type { ProductCardProps } from './ProductCard';
import { ProductCard } from './ProductCard';

interface MenuProductCardProps extends Omit<
  ProductCardProps,
  'onAddToBag' | 'specialInScope'
> {
  cart?: Cart | EmptyCart;
  isPdpProduct?: boolean;
  onPdpQuickAdd?: () => void;
  store: Store;
  visiblePriceIds?: PriceId[];
}

export const MenuProductCard = ({
  cart,
  store,
  isPdpProduct = false,
  onPdpQuickAdd,
  visiblePriceIds,
  ...props
}: MenuProductCardProps) => {
  const navigate = useNavigate();
  const { specialId } = useParams<OptionalMenuRouteParams>();
  const { pathname } = useLocation();
  const { searchState } = useSearchContext();
  const [queryParams] = useSearchParams();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(store.id, {
    enabled: !useV3 && !!store.id,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!store.id,
    storeId: store.id,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special, true)
  );

  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const matches = useMatches();

  const productAttributes = props.product.attributes;
  const isSponsored = props.product.isSponsored;

  const getProductPrice = useGetProductPrices(productAttributes, specials);
  const weights = useMemo(
    () => availableWeightsForMenuProduct(productAttributes, visiblePriceIds),
    [productAttributes, visiblePriceIds]
  );

  const {
    unitPrice,
    discountPrice,
    priceId: selectedWeight,
  } = getProductPrice(
    lowestPriceForMenuProduct(productAttributes, weights) || 'each'
  );

  const inventoryCount = getMaxCartQuantity(productAttributes, selectedWeight);
  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(unitPrice, discountPrice);
  }, [unitPrice, discountPrice]);

  const {
    inlineAds: { addInlineAdId },
  } = useProductTracking();

  const trackDmProducts = useCallback(() => {
    if (isSponsored) {
      addInlineAdId(productAttributes.product_id);
    }
  }, [isSponsored, addInlineAdId, productAttributes.product_id]);

  const onSuccess = useAddProductOnSuccess({
    callback: trackDmProducts,
    cart,
    discountPercentage,
    discountPrice,
    isInlineAd: isSponsored,
    product: productAttributes,
    quickAdd: true,
    selectedWeight,
    source: getSource({
      isTopper: false,
      matches,
      pathname,
    }),
    specialInScope,
    store,
    unitPrice,
  });

  const { onAddToBag, isLoading } = useAddProductToBag({
    cart,
    onSuccess,
    product: productAttributes,
    store,
    priceId: selectedWeight,
  });

  const onPress = useCallback(
    (product: SmartSortProduct<AlgoliaProduct>) => {
      if (weights.length > 1) {
        product.click();
        const pdpDepth = queryParams.get('pdpDepth');
        const newQueryParams = new URLSearchParams({
          priceId: selectedWeight ?? '',
          ...(isSponsored ? { inline: 'true' } : {}),
          ...(pdpDepth ? { pdpDepth: (Number(pdpDepth) + 1).toString() } : {}),
        });

        const pdpQuickAddPath = pathname.replace(
          /\/products\/\d+/,
          `/select-addon-weight/${
            productAttributes.product_id
          }?${newQueryParams.toString()}`
        );

        navigate(
          isPdpProduct
            ? pdpQuickAddPath
            : `${pathname}/select-addon-weight/${
                productAttributes.product_id
              }?${newQueryParams.toString()}`
        );
      } else {
        product.click({ isAddToCart: true });
        onAddToBag({});
        onPdpQuickAdd?.();
      }
    },
    [
      isPdpProduct,
      isSponsored,
      navigate,
      onAddToBag,
      onPdpQuickAdd,
      pathname,
      productAttributes.product_id,
      selectedWeight,
      weights.length,
    ]
  );

  const disableQuickAdd = useMemo(() => {
    if (weights.length > 1) {
      return false;
    }
    const existingCartProduct = (cart?.products ?? []).find(
      ({ price_id, product_id }) =>
        productAttributes.product_id === product_id &&
        price_id === selectedWeight
    );
    return (
      !!existingCartProduct &&
      existingCartProduct.count ===
        getMaxCartQuantity(existingCartProduct, existingCartProduct.price_id)
    );
  }, [
    cart?.products,
    productAttributes.product_id,
    selectedWeight,
    weights.length,
  ]);

  return (
    <ProductCard
      {...props}
      onAddToBag={({ productInstance }) => onPress(productInstance)}
      inventoryCount={inventoryCount}
      isLoading={isLoading}
      disableQuickAdd={disableQuickAdd}
      specialInScope={specialInScope}
      searchState={searchState}
      visiblePriceIds={visiblePriceIds}
    />
  );
};
